/* eslint-disable react-hooks/exhaustive-deps */

/**
 * A React functional component that imports necessary hooks and functions from various libraries.
 * @returns None
 */
import { debounce } from "lodash";
import { getCloudinaryMediaBaseUrl } from "../../../../config/cloudinaryBase";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import InQueueTabComponent from "./inQueueTabComponent";

import useWindowDimension from "../../../../components/hooks/useWindowDimension";
import { buildStaffEventTicketRequest } from "../../../../helpers/eventClubScope";
import { eventInQueueList } from "../../../../store/features/event/eventSlice";
import { showToast } from "../../../../store/features/toast/toastSlice";

const InQueueTabContainer = (props) => {
  /**
   * This component initializes various variables and states used in the order table view.
   * It also sets up references to DOM elements and hooks for handling window dimensions.
   * @returns None
   */
  const { t } = useTranslation();
  const eventID = props?.data?._id;
  const inQueueUserList = useSelector(
    (state) => state?.event?.eventInQueueListResponce
  );
  const tableContainerRef = useRef(null);
  const smarttableref = useRef();
  const dispatch = useDispatch();
  const dimensions = useWindowDimension();
  const pageSizeRef = useRef(15);
  const [loading, setLoading] = useState(false);
  const [breadcrumbHeight, setBreadcrumbHeight] = useState("");
  const [curruntEventDetailPageHeight, setCurruntEventDetailPageHeight] =
    useState("");
  const [showViewTicketModal, setShowViewTicketModal] = useState(false);
  const [loaderHtml, setLoaderHtmlHeight] = useState("");
  const [placeHolder, setPlaceHolder] = useState(true);

  /**
   * Sets up a state variable and an effect that updates the state variable after a delay.
   * @returns None
   */
  const closeViewTicketModal = () => setShowViewTicketModal(false);

  const getList = async () => {
    const req = buildStaffEventTicketRequest(eventID);
    if (!req) return;
    dispatch(eventInQueueList(req)).then((resultAction) => {
      if (eventInQueueList.fulfilled.match(resultAction)) {
        setPlaceHolder(false);
      } else if (eventInQueueList.rejected.match(resultAction)) {
        setPlaceHolder(false);
      }
    });
  };

  useEffect(() => {
    getList();
  }, [eventID]);

  /**
   * Clears the search input by programmatically clicking the clear search button.
   * @returns None
   */
  const ClearSearchButton = () => {
    smarttableref.current.querySelectorAll("button")[0].click();
  };

  /**
   * An array of column objects used for rendering a table.
   * Each column object represents a column in the table and contains properties such as label, key, filter, and sorting.
   * @type {Array<Object>}
   */
  const columns = [
    {
      label: <>{t("USER_DETAILS")}</>,
      key: "userid",
      filter: false,
      sorting: false,
    },
  ];

  /**
   * Handles the action of viewing a ticket.
   * @param {Event} e - The event object.
   * @param {Object} item - The ticket item to view.
   * @returns None
   */
  const onViewTicket = (e, item) => {
    setTimeout(() => {
      dispatch(
        showToast({
          show: false,
          message: t("User “Jerome Bell” has been deleted successfully!"),
          type: "success",
        })
      );
      closeViewTicketModal();
    }, 1000);
  };

  /**
   * useEffect hook that updates the heights of various elements on the page
   * when the loading state changes.
   * @param {{boolean}} loading - The loading state of the page.
   * @returns None
   */
  useEffect(() => {
    loading &&
      setLoaderHtmlHeight(
        document.getElementsByClassName("table-responsive")[0].offsetHeight
      );
    setBreadcrumbHeight(
      document.getElementsByClassName("page-title-box")[0].offsetHeight
    );
    setCurruntEventDetailPageHeight(
      document.getElementsByClassName("curruntEventDetailPage")[0].offsetHeight
    );
  }, [loading]);

  /**
   * Loads more items when triggered, with a delay of 2 seconds.
   * @returns None
   */
  const loadMoreItems = () => {
    if (loading) return;
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      pageSizeRef.current += 10;
    }, 2000);
  };

  /**
   * Debounces the handleTableScroll function to prevent it from being called too frequently.
   * When the table container is scrolled, it checks if the scroll position is at the bottom
   * and if there are more items in the queue to load. If both conditions are met, it calls
   * the loadMoreItems function.
   * @returns None
   */
  const handleTableScroll = debounce(() => {
    const tableContainer = tableContainerRef.current;
    if (tableContainer) {
      const isAtBottom =
        tableContainer.scrollTop + tableContainer.clientHeight >=
        tableContainer.scrollHeight;
      if (isAtBottom && inQueueUserList?.length > pageSizeRef.current) {
        loadMoreItems();
      }
    }
  }, 200);

  /**
   * Adds an event listener to the table container element to handle scroll events.
   * The event listener is removed when the component is unmounted.
   * @param {Function} handleTableScroll - The function to handle the table scroll event.
   * @returns None
   */
  useEffect(() => {
    const tableContainer = tableContainerRef.current;
    if (tableContainer) {
      tableContainer.addEventListener("scroll", handleTableScroll);
    }
    return () => {
      if (tableContainer) {
        tableContainer.removeEventListener("scroll", handleTableScroll);
      }
    };
  }, [handleTableScroll]);

  /**
   * Constructs the Cloudinary URL by concatenating the environment variables
   * for the Cloudinary URL, name, and subfolder.
   * @returns The constructed Cloudinary URL.
   */
  const CLOUDINARY_URL = getCloudinaryMediaBaseUrl();

  return (
    <InQueueTabComponent
      showViewTicketModal={showViewTicketModal}
      smarttableref={smarttableref}
      closeViewTicketModal={() => {
        setShowViewTicketModal(false);
      }}
      onViewTicket={(e, item) => onViewTicket(e, item)}
      columns={columns}
      inQueueData={inQueueUserList}
      clearSearchButton={ClearSearchButton}
      emptyImg={CLOUDINARY_URL + "noticket.png"}
      emptyTitle={t("NO_USER_IN_QUEUE")}
      noDataFound={t("NO_DATA_FOUND")}
      noDataTitle={t("NO_DATA_FOUND")}
      noDataDesc={t(
        "YOUR_SEARCH_DID_NOT_MATCH_ANY_USER_NAMES_PLEASE_TRY_SEARCHING_AGAIN"
      )}
      emptyDataDescTow={t("NO_USER_ARE_IN_QUEUE")}
      noDataAddButtonTitle={t("ADD_USER")}
      loaderHtml={loaderHtml}
      placeHolderShimer={placeHolder}
      itemsPerPage={pageSizeRef.current}
      dimensions={dimensions}
      breadcrumbHeight={breadcrumbHeight}
      curruntEventDetailPageHeight={curruntEventDetailPageHeight}
      loading={loading}
      tableContainerRef={tableContainerRef}
    />
  );
};

export default InQueueTabContainer;
